import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
  DocumentUploadFormGroup,
  getDocumentUploadFormGroup,
} from '@app/components/registration/document-upload-form/document-upload-form.helper';
import { formatDate, formatTime } from '@app/helpers/formatDateTime.helper';
import { createKeyedLoader } from '@app/helpers/loader';
import { LegalEntityWithUser } from '@app/models/project.models';
import { LegalEntityService } from '@app/services/admin/legal-entity.service';
import { DownloadService } from '@app/services/file/download.service';
import { AuthStore } from '@app/stores/auth.store';
import { GenericStore } from '@app/stores/generic.store';
import { ButtonComponent } from '@app/ui-components/button/button.component';
import { CardComponent } from '@app/ui-components/card/card.component';
import { customRendererCellStyle } from '@app/ui-components/grid-table/custom-renderer-cell-style';
import { GridTableComponent } from '@app/ui-components/grid-table/grid-table.component';
import { TwoLineCellRendererComponent } from '@app/ui-components/grid-table/two-line-cell-renderer/two-line-cell-renderer.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  ColDef,
  ICellRendererParams,
  IDatasource,
  IGetRowsParams,
} from 'ag-grid-community';
import { map } from 'rxjs';

type Row = {
  id: string;
  date: string;
  createdDate: string;
  createdTime: string;
  title: string;
  subtitle: string;
  documentId?: string;
  docStatus?: 'pending' | 'approved' | 'rejected';
  enabledConnection?: boolean | null;
  userId?: string;
};
export interface ApprovalRequest {
  id: string;
  userId?: string;
  userEmail?: string | null;
  requestedAt?: string;
  isApproved?: boolean | null;
  status?: string | null;
  [key: string]: unknown;
}

@Component({
  selector: 'app-representatives',
  templateUrl: './representatives.component.html',
  styleUrl: './representatives.component.scss',
  imports: [TranslatePipe, ButtonComponent, GridTableComponent, CardComponent],
})
export class RepresentativesComponent implements OnInit {
  protected store = inject(AuthStore);
  protected genericStore = inject(GenericStore);
  documentUpload: FormGroup<DocumentUploadFormGroup>;

  private legalEntityId = computed(() => this.store.loggedInCompanyId());

  constructor(
    fb: FormBuilder,
    private legalEntityService: LegalEntityService,
  ) {
    this.documentUpload = getDocumentUploadFormGroup(fb);
  }

  columns: ColDef[] = [];
  rowModelType: 'infinite' = 'infinite';
  cacheBlockSize = 10;
  maxBlocksInCache = 5;
  rowHeight = 56;
  datasource!: IDatasource;

  private readonly downloadService = inject(DownloadService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly searchTerm = signal<string>('');
  readonly isLoading = signal<boolean>(false);
  private readonly loader = createKeyedLoader();

  ngOnInit(): void {
    this.buildColumns();
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.buildColumns());
    if (this.legalEntityId()) this.datasource = this.buildDatasource();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['legalEntityId'] && this.legalEntityId()) {
      this.datasource = this.buildDatasource();
    }
  }

  save() {
    throw new Error('Method not implemented.');
  }
  cancelEdit() {
    throw new Error('Method not implemented.');
  }

  private buildColumns(): void {
    this.columns = [
      {
        field: 'date',
        headerName: this.translate.instant('admin.legalEntities.table.date'),
        cellRenderer: TwoLineCellRendererComponent,
        cellRendererParams: (param: ICellRendererParams<Row>) => ({
          title: param.data?.createdDate ?? '-',
          subtitle: param.data?.createdTime ?? '',
        }),
        cellStyle: customRendererCellStyle,
        width: 100,
        flex: 0,
        suppressNavigable: true,
        sortable: false,
        filter: false,
      },
      {
        field: 'user',
        headerName: this.translate.instant('admin.legalEntities.table.user'),
        cellRenderer: TwoLineCellRendererComponent,
        cellRendererParams: (param: ICellRendererParams<Row>) => ({
          title: param.data?.title ?? '',
          subtitle: param.data?.subtitle ?? '',
        }),
        cellStyle: customRendererCellStyle,
        sortable: false,
        filter: false,
      },
    ];
  }

  private buildDatasource(): IDatasource {
    return {
      rowCount: undefined,
      getRows: (params: IGetRowsParams) => {
        const legalEntityId = this.legalEntityId();
        if (!legalEntityId) return params.successCallback([], 0);

        this.isLoading.set(true);
        const search = this.searchTerm().trim().toLowerCase();

        this.legalEntityService
          .getApprovalRequestsByLegalEntity(legalEntityId)
          .pipe(
            map((list: unknown): LegalEntityWithUser[] =>
              Array.isArray(list) ? (list as LegalEntityWithUser[]) : [],
            ),
            map((requestList): Row[] =>
              requestList.map((request) => {
                const user = request.user ?? null;
                const personal = user?.personalData ?? null;

                const fullName = [
                  personal?.firstName,
                  personal?.midName,
                  personal?.lastName,
                ]
                  .filter(Boolean)
                  .join(' ')
                  .trim();

                const email = user?.email ?? personal?.email ?? '';
                const title = fullName ?? '-';
                const subtitle = email ?? '';

                const date = request.decisionDate ?? request.creationDate ?? '';

                return {
                  id: request.id,
                  date: date ?? '',
                  createdDate: formatDate(date ?? ''),
                  createdTime: formatTime(date ?? ''),
                  title,
                  subtitle,
                  documentId: user?.verifDocumentId ?? undefined,
                  docStatus: 'pending',
                  enabledConnection: request.enabledConnection ?? null,
                  userId: user?.id ?? undefined,
                };
              }),
            ),

            map((rows) =>
              search
                ? rows.filter((row) =>
                    [
                      row.date,
                      row.createdDate,
                      row.createdTime,
                      row.title,
                      row.subtitle,
                      row.documentId ?? '',
                    ]
                      .join(' ')
                      .toLowerCase()
                      .includes(search),
                  )
                : rows,
            ),
          )
          .subscribe({
            next: (rows) => params.successCallback(rows, rows.length),
            error: () => params.successCallback([], 0),
            complete: () => this.isLoading.set(false),
          });
      },
    };
  }
}
